const MAX_PHOTO_BYTES = 20 * 1024 * 1024;
const IMAGE_TYPES = new Map([
  ['image/jpeg', /\.jpe?g$/i],
  ['image/png', /\.png$/i],
  ['image/webp', /\.webp$/i]
]);
const FONT_FAMILIES = new Map([
  ['Classic serif', 'Georgia, "Times New Roman", serif'],
  ['Clean sans serif', 'Arial, Helvetica, sans-serif'],
  ['Script', '"Brush Script MT", "Segoe Script", cursive']
]);

export function referenceFileError(file) {
  if (!file) return null;
  if (!IMAGE_TYPES.get(file.type)?.test(file.name)) return 'fileType';
  if (file.size > MAX_PHOTO_BYTES) return 'fileSize';
  if (file.size === 0) return 'fileImage';
  return null;
}

export function matchingVariant(variants, options) {
  return variants.find(variant => variant.options.every((value, index) => value === options[index]));
}

class ProductDialog {
  constructor(dialog, preferredFocus) {
    this.dialog = dialog;
    this.preferredFocus = preferredFocus;
    this.returnFocus = null;
    dialog.addEventListener('close', () => this.onClose());
    dialog.addEventListener('keydown', event => this.trapFocus(event));
    dialog.addEventListener('click', event => this.onBackdrop(event));
  }

  open(trigger = document.activeElement) {
    this.returnFocus = trigger;
    if (!this.dialog.open) this.dialog.showModal();
    document.documentElement.classList.add('paw-dialog-open');
    (this.dialog.querySelector(this.preferredFocus) || this.focusable()[0])?.focus();
  }

  close() {
    this.dialog.close();
  }

  onClose() {
    if (!document.querySelector('dialog[open]')) document.documentElement.classList.remove('paw-dialog-open');
    if (this.returnFocus?.isConnected) this.returnFocus.focus({preventScroll: true});
  }

  focusable() {
    return [...this.dialog.querySelectorAll('button, input, select, textarea, a[href], [tabindex]')]
      .filter(element => !element.disabled && element.tabIndex >= 0 && element.getClientRects().length);
  }

  trapFocus(event) {
    if (event.key !== 'Tab') return;
    const elements = this.focusable();
    const first = elements[0];
    const last = elements.at(-1);
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first?.focus();
    }
  }

  onBackdrop(event) {
    if (event.target !== this.dialog) return;
    const bounds = this.dialog.getBoundingClientRect();
    const outside = event.clientX < bounds.left || event.clientX > bounds.right
      || event.clientY < bounds.top || event.clientY > bounds.bottom;
    if (outside) this.close();
  }
}

class ProductGallery {
  constructor(section) {
    this.section = section;
    this.track = section.querySelector('[data-gallery-track]');
    this.slides = [...this.track.querySelectorAll('[data-gallery-index]')];
    this.thumbnails = [...section.querySelectorAll('[data-gallery-thumbnail]')];
    this.counter = section.querySelector('[data-gallery-counter]');
    this.previous = section.querySelector('[data-gallery-previous]');
    this.next = section.querySelector('[data-gallery-next]');
    this.lightbox = section.querySelector('[data-gallery-lightbox]');
    this.index = 0;
    this.lightboxIndex = 0;
    this.dialog = typeof this.lightbox.showModal === 'function'
      ? new ProductDialog(this.lightbox, '[data-lightbox-close]') : null;
    this.bind();
    this.resize = new ResizeObserver(() => this.update());
    this.resize.observe(this.track);
    this.update();
  }

  bind() {
    this.track.addEventListener('scroll', () => this.update(), {passive: true});
    this.previous?.addEventListener('click', () => this.go(this.index - 1));
    this.next?.addEventListener('click', () => this.go(this.index + 1));
    this.slides.forEach((slide, index) => slide.addEventListener('click', () => this.openImage(index, slide)));
    this.thumbnails.forEach((thumbnail, index) => thumbnail.addEventListener('click', () => this.go(index)));
    this.track.addEventListener('keydown', event => this.onArrow(event));
    this.lightbox.querySelector('[data-lightbox-close]').addEventListener('click', () => this.dialog?.close());
    this.lightbox.querySelector('[data-lightbox-prev]').addEventListener('click', () => this.showImage(this.lightboxIndex - 1));
    this.lightbox.querySelector('[data-lightbox-next]').addEventListener('click', () => this.showImage(this.lightboxIndex + 1));
    this.lightbox.addEventListener('keydown', event => {
      if (event.key === 'ArrowLeft') this.showImage(this.lightboxIndex - 1);
      if (event.key === 'ArrowRight') this.showImage(this.lightboxIndex + 1);
    });
  }

  step() {
    if (!this.slides.length) return 1;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    return this.slides[0].getBoundingClientRect().width + gap;
  }

  maxIndex() {
    return Math.max(0, Math.round((this.track.scrollWidth - this.track.clientWidth) / this.step()));
  }

  update() {
    this.index = Math.min(this.maxIndex(), Math.round(this.track.scrollLeft / this.step()));
    if (this.counter) this.counter.textContent = String(this.index + 1) + ' / ' + this.slides.length;
    if (this.previous) this.previous.disabled = this.index === 0;
    if (this.next) this.next.disabled = this.index >= this.maxIndex();
    this.thumbnails.forEach((thumbnail, index) => thumbnail.setAttribute('aria-pressed', String(index === this.index)));
  }

  go(index) {
    const target = Math.max(0, Math.min(index, this.maxIndex()));
    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    this.track.scrollTo({left: target * this.step(), behavior});
  }

  onArrow(event) {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    this.go(this.index + (event.key === 'ArrowLeft' ? -1 : 1));
  }

  openImage(index, trigger) {
    if (!this.dialog) {
      window.open(this.slides[index].dataset.galleryImage, '_blank', 'noopener');
      return;
    }
    this.showImage(index);
    this.dialog.open(trigger);
  }

  showImage(index) {
    if (!this.slides.length) return;
    this.lightboxIndex = (index + this.slides.length) % this.slides.length;
    const slide = this.slides[this.lightboxIndex];
    const image = this.lightbox.querySelector('[data-lightbox-image]');
    image.src = slide.dataset.galleryImage;
    image.alt = slide.dataset.imageAlt;
    this.lightbox.querySelector('[data-lightbox-counter]').textContent
      = String(this.lightboxIndex + 1) + ' / ' + this.slides.length;
  }

  selectVariantImage(image) {
    if (!image?.src) return;
    const target = new URL(image.src, window.location.href).pathname;
    const index = this.slides.findIndex(slide => new URL(slide.dataset.galleryImage, window.location.href).pathname === target);
    if (index >= 0) this.go(index);
  }

  destroy() {
    this.resize.disconnect();
    if (this.lightbox.open) this.dialog?.close();
  }
}

class PhotoUpload {
  constructor(slot, strings, onChange) {
    this.slot = slot;
    this.strings = strings;
    this.onChange = onChange;
    this.input = slot.querySelector('[data-photo-input]');
    this.preview = slot.querySelector('[data-photo-preview]');
    this.thumbnail = slot.querySelector('[data-photo-thumbnail]');
    this.error = slot.querySelector('[data-photo-error]');
    this.url = null;
    this.input.addEventListener('change', () => this.show());
    slot.querySelector('[data-photo-remove]').addEventListener('click', () => this.remove());
    this.bindDrop();
  }

  bindDrop() {
    for (const eventName of ['dragenter', 'dragover']) {
      this.slot.addEventListener(eventName, event => {
        event.preventDefault();
        if (!this.input.disabled) this.slot.classList.add('is-dragging');
      });
    }
    for (const eventName of ['dragleave', 'drop']) {
      this.slot.addEventListener(eventName, event => {
        event.preventDefault();
        this.slot.classList.remove('is-dragging');
        if (eventName === 'drop' && !this.input.disabled) this.receive(event.dataTransfer?.files);
      });
    }
  }

  receive(files) {
    if (!files?.length) return;
    const transfer = new DataTransfer();
    transfer.items.add(files[0]);
    this.input.files = transfer.files;
    this.show();
  }

  resetPreview() {
    if (this.url) URL.revokeObjectURL(this.url);
    this.url = null;
    this.preview.hidden = true;
    this.thumbnail.removeAttribute('src');
    this.slot.classList.remove('has-photo');
    this.input.setCustomValidity('');
    this.input.removeAttribute('aria-invalid');
    this.error.hidden = true;
    this.error.textContent = '';
    this.onChange?.(null);
  }

  fail(key) {
    const message = this.strings[key];
    this.input.setCustomValidity(message);
    this.input.setAttribute('aria-invalid', 'true');
    this.error.textContent = message;
    this.error.hidden = false;
  }

  show() {
    this.resetPreview();
    const file = this.input.files[0];
    if (!file) return;
    const error = referenceFileError(file);
    if (error) return this.fail(error);
    this.input.setCustomValidity(this.strings.fileImage);
    this.url = URL.createObjectURL(file);
    const activeUrl = this.url;
    this.thumbnail.onload = () => this.onLoad(activeUrl);
    this.thumbnail.onerror = () => { if (this.url === activeUrl) this.fail('fileImage'); };
    this.slot.querySelector('[data-photo-filename]').textContent = file.name;
    this.thumbnail.src = this.url;
  }

  onLoad(activeUrl) {
    if (this.url !== activeUrl) return;
    this.input.setCustomValidity('');
    this.preview.hidden = false;
    this.slot.classList.add('has-photo');
    this.onChange?.(this.url);
  }

  remove() {
    this.input.value = '';
    this.resetPreview();
    this.input.focus();
  }

  destroy() {
    if (this.url) URL.revokeObjectURL(this.url);
  }
}

class PersonalizationPreview {
  constructor(section) {
    this.section = section;
    this.overlay = section.querySelector('[data-lettering-preview]');
    this.inputs = new Map([
      ['name', section.querySelector('[data-personalization-name]')],
      ['dates', section.querySelector('[data-personalization-dates]')],
      ['message', section.querySelector('[data-personalization-message]')]
    ]);
    this.font = section.querySelector('[data-personalization-font]');
    this.inputs.forEach(input => input?.addEventListener('input', () => this.update()));
    this.font?.addEventListener('change', () => this.update());
    this.update();
  }

  update() {
    this.inputs.forEach((input, key) => {
      const target = this.section.querySelector('[data-preview-' + key + ']');
      if (target) target.textContent = input?.value || '';
    });
    this.overlay.style.fontFamily = FONT_FAMILIES.get(this.font?.value) || FONT_FAMILIES.get('Classic serif');
  }

  reference(url) {
    const holder = this.section.querySelector('[data-preview-reference]');
    const image = holder.querySelector('img');
    holder.hidden = !url;
    if (url) image.src = url;
    else image.removeAttribute('src');
  }
}

class PawProduct {
  constructor(section) {
    this.section = section;
    this.form = section.querySelector('form');
    this.strings = JSON.parse(section.querySelector('[data-product-strings]').textContent);
    this.variants = JSON.parse(section.querySelector('[data-product-variants]').textContent);
    this.prices = JSON.parse(section.querySelector('[data-product-prices]').textContent);
    this.variantSelect = section.querySelector('[data-variant-id]');
    this.button = section.querySelector('[data-add-to-cart]');
    this.openButton = section.querySelector('[data-personalize-open]');
    this.error = section.querySelector('[data-product-error]');
    this.status = section.querySelector('[data-product-status]');
    this.busy = false;
    this.gallery = new ProductGallery(section);
    this.preview = new PersonalizationPreview(section);
    this.photos = [...section.querySelectorAll('[data-upload-slot]')].map((slot, index) => {
      return new PhotoUpload(slot, this.strings, index === 0 ? url => this.preview.reference(url) : null);
    });
    this.setupDialog();
    this.bind();
    section.classList.add('is-enhanced');
  }

  setupDialog() {
    const dialog = this.section.querySelector('[data-personalize-dialog]');
    if (typeof dialog.showModal !== 'function') {
      this.openButton.hidden = true;
      return;
    }
    dialog.append(this.section.querySelector('[data-customizer-panel]'));
    this.dialog = new ProductDialog(dialog, '[data-personalization-name]');
    dialog.querySelector('[data-personalize-close]').addEventListener('click', () => this.dialog.close());
    this.openButton.addEventListener('click', () => this.dialog.open(this.openButton));
  }

  bind() {
    this.section.querySelectorAll('[data-option-position] input').forEach(input => {
      input.addEventListener('change', () => this.updateVariant());
    });
    this.form.addEventListener('submit', event => this.submit(event));
  }

  updateVariant() {
    const groups = [...this.section.querySelectorAll('[data-option-position]')];
    const options = groups.map(group => group.querySelector('input:checked')?.value);
    groups.forEach((group, index) => {
      const label = group.querySelector('[data-option-selected]');
      if (label) label.textContent = options[index] || '';
    });
    const variant = matchingVariant(this.variants, options);
    this.variantSelect.value = variant?.id || '';
    this.updateButtons(variant);
    this.error.hidden = true;
    if (!variant) return;
    this.updatePrice(variant);
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);
  }

  updateButtons(variant) {
    const available = Boolean(variant?.available);
    this.button.disabled = this.busy || !available;
    this.openButton.disabled = this.busy || !available;
    this.button.textContent = variant ? (available ? this.strings.add : this.strings.soldOut) : this.strings.unavailable;
    this.openButton.textContent = variant ? (available ? this.strings.personalize : this.strings.soldOut) : this.strings.unavailable;
  }

  updatePrice(variant) {
    const price = this.prices[variant.id];
    this.section.querySelector('[data-product-price]').textContent = price.price;
    this.section.querySelector('[data-modal-price]').textContent = price.price;
    const compare = this.section.querySelector('[data-product-compare]');
    compare.hidden = !price.compare;
    compare.textContent = price.compare || '';
    this.gallery.selectVariantImage(variant.featured_image);
  }

  setBusy(busy) {
    this.busy = busy;
    this.form.setAttribute('aria-busy', String(busy));
    if (busy) this.controls = [...this.form.elements].map(control => ({control, disabled: control.disabled}));
    this.controls?.forEach(({control, disabled}) => { control.disabled = busy || disabled; });
    const variant = this.variants.find(item => String(item.id) === this.variantSelect.value);
    this.updateButtons(variant);
    if (busy) this.button.textContent = this.strings.adding;
    this.status.textContent = busy ? this.strings.uploading : '';
  }

  async submit(event) {
    event.preventDefault();
    if (this.busy || !this.form.reportValidity() || !this.variantSelect.value) return;
    const data = new FormData(this.form);
    this.photos.forEach(photo => { if (!photo.input.files.length) data.delete(photo.input.name); });
    this.error.hidden = true;
    this.setBusy(true);
    try {
      const response = await fetch(this.section.dataset.addUrl, {method: 'POST', body: data, headers: {'Accept': 'application/json'}});
      const result = await response.json();
      if (!response.ok) throw new Error(typeof result.description === 'string' ? result.description : this.strings.error);
      window.location.assign(this.section.dataset.cartUrl);
    } catch (error) {
      this.error.textContent = error instanceof TypeError ? this.strings.error : error.message || this.strings.error;
      this.error.hidden = false;
      this.setBusy(false);
    }
  }

  destroy() {
    this.photos.forEach(photo => photo.destroy());
    this.gallery.destroy();
    if (this.dialog?.dialog.open) this.dialog.close();
  }
}

const productInstances = new WeakMap();

function initializeProducts(scope = document) {
  scope.querySelectorAll('[data-paw-product]').forEach(section => {
    if (productInstances.has(section)) return;
    const product = new PawProduct(section);
    productInstances.set(section, product);
    section.dataset.initialized = 'true';
  });
}

if (typeof document !== 'undefined') {
  initializeProducts();
  document.addEventListener('shopify:section:load', event => initializeProducts(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('[data-paw-product]').forEach(section => productInstances.get(section)?.destroy());
  });
}
